// src/components/member/UserSoalProfiles.jsx
import React, { useEffect, useRef, useState } from "react";
import "./SharedMember.css";
import "./UserSoalProfiles.css";
import AnnouncementPopup from "./announcements/AnnouncementPopup";
import Voters from "./electionperiod/Voters";
import CropModal from "./CropModal";
import imageCompression from "browser-image-compression";
import UserCalendar from "./calendar/UserCalendar";
import PostFeed from "./PostFeed";
import { clearAuthDisplay, setAuthDisplay } from "../../authDisplay";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production"
        ? "https://api.soaltriskelions.com"
        : "http://localhost:5000");

const PUBLIC = process.env.PUBLIC_URL || "";
const FRONTEND_URL =
    process.env.REACT_APP_FRONTEND_URL ??
    (process.env.NODE_ENV === "production"
        ? "https://soaltriskelions.com"
        : "http://localhost:3000");

// Label mapping
const labelMap = {
    primaryid: "Primary ID",
    firstname: "First Name",
    lastname: "Last Name",
    alias: "Alias",
    birthsurvive: "Birth/Survive",
    batchname: "Batch Name",
    rootchapter: "Root Chapter",
    gtglt: "GT/GLT",
    mwwmi: "MWW/MI",
    welcome: "Welcome To",
    domname: "Verified By",
    yearserved: "Year Served",
};

// Cache-busted photo URL
const getPhotoPath = (tab, primaryid) => {
    if (!primaryid) return null;
    const ts = Date.now();

    if (tab === "Phi")
        return `${API_BASE}/UploadPhoto/Phi-Photo/${primaryid}.jpg?t=${ts}`;
    if (tab === "Sigma")
        return `${API_BASE}/UploadPhoto/Sigma-Photo/${primaryid}.jpg?t=${ts}`;
    if (tab === "Chairman")
        return `${API_BASE}/api/legacy/photo/${encodeURIComponent(primaryid)}?t=${ts}`;

    return null;
};

export default function UserSoalProfiles({ role = "user", isAdminPreview = false, onExitPreview }) {
    const shouldOpenMembersView = () => {
        try {
            const params = new URLSearchParams(window.location.search);
            return params.get("view") === "members" || localStorage.getItem("footer_member_target") === "profile";
        } catch {
            return false;
        }
    };

    const clearMembersViewTarget = () => {
        try {
            localStorage.removeItem("footer_member_target");
        } catch {
            // Ignore storage cleanup issues.
        }
    };

    const [tab, setTab] = useState("Phi");
    const [view, setView] = useState("myprofile");
    const [currentUser, setCurrentUser] = useState(null);
    const [currentMember, setCurrentMember] = useState(null);
    const [myEdit, setMyEdit] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMsg, setProfileMsg] = useState("");
    const [profiles, setProfiles] = useState({
        Phi: [],
        Sigma: [],
        Chairman: [],
    });
    const [loadedProfiles, setLoadedProfiles] = useState({
        Phi: false,
        Sigma: false,
        Chairman: false,
    });
    const [reports, setReports] = useState([]);
    const [search, setSearch] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [selectedProfileTab, setSelectedProfileTab] = useState(null);
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(1);
    const [enlargedImage, setEnlargedImage] = useState(null);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [mobileNavTooltipVisible, setMobileNavTooltipVisible] = useState(false);
    const mobileNavTooltipTimerRef = useRef(null);

    const [cropFile, setCropFile] = useState(null);
    const [croppingProfile, setCroppingProfile] = useState(null);

    const [removing, setRemoving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Exit unauthorized users
    useEffect(() => {
        if (role !== "user" && !isAdminPreview) window.location.replace(FRONTEND_URL);
    }, [role, isAdminPreview]);

    const handleViewChange = (v) => {
        setView(v);
        if (!isAdminPreview) localStorage.setItem("lastView", v);
    };

    const handleMobileViewChange = (v) => {
        handleViewChange(v);
        setMobileNavOpen(false);
    };

    const showTemporaryMobileNavTooltip = () => {
        setMobileNavTooltipVisible(true);
        if (mobileNavTooltipTimerRef.current) {
            clearTimeout(mobileNavTooltipTimerRef.current);
        }
        mobileNavTooltipTimerRef.current = setTimeout(() => {
            setMobileNavTooltipVisible(false);
        }, 1000);
    };

    const handleMobileNavToggle = () => {
        setMobileNavOpen((open) => !open);
        showTemporaryMobileNavTooltip();
    };

    useEffect(() => {
        return () => {
            if (mobileNavTooltipTimerRef.current) {
                clearTimeout(mobileNavTooltipTimerRef.current);
            }
        };
    }, []);

    const loadMyProfile = async () => {
        const token = localStorage.getItem("token");

        const res = await fetch(`${API_BASE}/api/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        if (!res.ok || !data.ok) {
            setProfileMsg(data?.error || "Your login is not linked to a member profile yet.");
            return null;
        }

        setCurrentUser(data.user);
        setCurrentMember(data.member);
        setAuthDisplay({
            role: data.user?.role || role,
            idname: data.user?.idname || "",
            firstName: data.member?.firstname || "",
            lastName: data.member?.lastname || "",
        });
        setTab(data.member.gender || "Phi");
        setMyEdit({
            lastName: data.member.lastname || "",
            firstName: data.member.firstname || "",
            alias: data.member.alias || "",
            birthSurvive: data.member.birthsurvive || "",
            batchName: data.member.batchname || "",
            rootchapter: data.member.rootchapter || "",
            gtglt: data.member.gtglt || "",
            mwwmi: data.member.mwwmi || "",
            welcome: data.member.welcome || "",
            domName: data.member.domname || "",
            mobile: data.user?.mobile || "",
            email: data.user?.email || "",
        });

        return data.member;
    };

    // Initial load
    useEffect(() => {
        const token = localStorage.getItem("token");

        (async () => {
            const member = await loadMyProfile();
            if (isAdminPreview) {
                if (shouldOpenMembersView()) {
                    setView("profile");
                    clearMembersViewTarget();
                } else {
                    setView(member ? "myprofile" : "profile");
                }
            }
        })();

        fetch(`${API_BASE}/api/reports/public`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.ok) setReports(data.files);
            });

    }, [isAdminPreview]);

    useEffect(() => {
        setSortOrder(tab === "Chairman" ? "year-desc" : "asc");
        setPage(1);
    }, [tab]);

    useEffect(() => {
        const savedTab = localStorage.getItem("lastTab");
        if (savedTab) setTab(savedTab);
    }, []);

    useEffect(() => {
        if (isAdminPreview) return;
        if (shouldOpenMembersView()) {
            setView("profile");
            clearMembersViewTarget();
            return;
        }
        const saved = localStorage.getItem("lastView");
        if (["myprofile", "profile", "postfeed", "report", "voting", "calendar"].includes(saved)) {
            setView(saved);
        } else {
            setView("myprofile");
        }
    }, [isAdminPreview]);

    useEffect(() => {
        if (profileMsg !== "Profile saved.") return undefined;

        const timer = setTimeout(() => {
            setProfileMsg("");
        }, 10000);

        return () => clearTimeout(timer);
    }, [profileMsg]);

    const updateMyEdit = (key, value) => {
        setMyEdit((prev) => ({ ...(prev || {}), [key]: value }));
    };

    const saveMyProfile = async () => {
        if (!myEdit) return;
        setSavingProfile(true);
        setProfileMsg("");

        const email = (myEdit.email || "").trim();
        const mobile = (myEdit.mobile || "").trim();

        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setProfileMsg("Please enter a valid email address.");
            setSavingProfile(false);
            return;
        }

        if (mobile && !/^\d+$/.test(mobile)) {
            setProfileMsg("Mobile number must contain numbers only.");
            setSavingProfile(false);
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE}/api/me`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(myEdit),
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setProfileMsg(data?.error || "Profile update failed.");
                return;
            }

            setCurrentMember(data.member);
            if (data.user) setCurrentUser(data.user);
            setIsEditingProfile(false);
            setProfileMsg("Profile saved.");
            await fetchData(data.member.gender || "Phi");
        } catch {
            setProfileMsg("Network error while saving profile.");
        } finally {
            setSavingProfile(false);
        }
    };

    // Fetch profiles
    const fetchData = async (gender) => {
        try {
            const url =
                gender === "Chairman"
                    ? `${API_BASE}/api/legacy?limit=5000`
                    : `${API_BASE}/api/members?gender=${gender}&limit=5000`;

            const token = localStorage.getItem("token");
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const data = await res.json();
            if (data.ok) {
                setProfiles((prev) => ({
                    ...prev,
                    [gender]: data.rows,
                }));
                setLoadedProfiles((prev) => ({
                    ...prev,
                    [gender]: true,
                }));
                return data.rows;
            }
        } catch (err) {
            console.error(`Failed to fetch ${gender}`, err);
        }
        return [];
    };

    useEffect(() => {
        if (view !== "profile") return;
        if (loadedProfiles[tab]) return;
        fetchData(tab);
    }, [view, tab, loadedProfiles]);

    // Refresh profile after upload/delete
    const refreshSingleProfile = async (primaryid) => {
        const gender = tab;
        let url =
            gender === "Chairman"
                ? `${API_BASE}/api/legacy?limit=5000`
                : `${API_BASE}/api/members?gender=${gender}&limit=5000`;

        const token = localStorage.getItem("token");
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        if (!data.ok) return;

        const updated = data.rows;
        setProfiles((p) => ({
            ...p,
            [gender]: updated,
        }));
        setLoadedProfiles((p) => ({
            ...p,
            [gender]: true,
        }));

        const match = updated.find((x) => x.primaryid === primaryid);
        if (match) {
            setSelectedProfileTab(gender);
            setSelectedProfile(match);
        }
    };

    const openPostFeedMemberProfile = async (person) => {
        const primaryId = String(person?.author_primaryid || "").trim();
        if (!primaryId) return;

        const explicitGender =
            person?.author_gender === "Sigma" || person?.author_gender === "Phi"
                ? person.author_gender
                : null;
        const inferredGender = primaryId.toUpperCase().startsWith("SIG-") ? "Sigma" : "Phi";
        const gender = explicitGender || inferredGender;
        const sourceProfiles = loadedProfiles[gender] ? profiles[gender] : await fetchData(gender);
        const match = sourceProfiles?.find(
            (profile) => String(profile.primaryid || "").toLowerCase() === primaryId.toLowerCase()
        );

        if (!match) return;

        setSelectedProfileTab(gender);
        setSelectedProfile(match);
    };

    // Upload (open crop modal)
    const handlePhotoUpload = (e, profile) => {
        if (tab === "Chairman") return;
        const file = e.target.files[0];
        if (!file) return;

        setCroppingProfile(profile);
        setCropFile(file);
    };

    // FINISH CROP + UPLOAD
    const finishCropAndUpload = async (file, croppedAreaPixels) => {
        if (!croppingProfile) return;

        setUploading(true);
        setUploadProgress(15);

        // 1. Crop
        const croppedImage = await getCroppedImage(file, croppedAreaPixels);
        setUploadProgress(40);

        // 2. Compress
        const compressedFile = await imageCompression(croppedImage, {
            maxSizeMB: 0.4,
            maxWidthOrHeight: 800,
            useWebWorker: true,
        });
        setUploadProgress(60);

        // 3. Upload
        const formData = new FormData();
        formData.append("photo", compressedFile);

        const endpoint =
            croppingProfile._own
                ? `${API_BASE}/api/me/photo`
                : tab === "Phi"
                ? `${API_BASE}/api/members/upload-photo/phi/${croppingProfile.primaryid}`
                : `${API_BASE}/api/members/upload-photo/sigma/${croppingProfile.primaryid}`;

        const token = localStorage.getItem("token");

        const interval = setInterval(() => {
            setUploadProgress((p) => Math.min(95, p + 3));
        }, 150);

        const res = await fetch(endpoint, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });

        clearInterval(interval);
        setUploadProgress(100);

        const data = await res.json();
        if (data.ok) {
            if (croppingProfile._own) {
                await loadMyProfile();
            } else {
                await refreshSingleProfile(croppingProfile.primaryid);
            }
        }

        setTimeout(() => {
            setUploading(false);
            setUploadProgress(0);
        }, 500);

        // close crop modal
        setCropFile(null);
        setCroppingProfile(null);
    };

    // Actual crop function
    async function getCroppedImage(file, cropPixels) {
        const img = await createImageBitmap(file);
        const canvas = document.createElement("canvas");
        canvas.width = cropPixels.width;
        canvas.height = cropPixels.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(
            img,
            cropPixels.x,
            cropPixels.y,
            cropPixels.width,
            cropPixels.height,
            0,
            0,
            cropPixels.width,
            cropPixels.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
        });
    }

    // DELETE photo
    const handleRemovePhoto = async (profile) => {
        if (tab === "Chairman") return;

        if (!window.confirm("Are you sure you want to delete this photo?")) return;

        setRemoving(true);

        const endpoint =
            tab === "Phi"
                ? `${API_BASE}/api/members/remove-photo/phi/${profile.primaryid}`
                : `${API_BASE}/api/members/remove-photo/sigma/${profile.primaryid}`;

        const res = await fetch(endpoint, { method: "DELETE" });
        const data = await res.json();

        if (data.ok) await refreshSingleProfile(profile.primaryid);

        setRemoving(false);
    };

    const handleOwnPhotoUpload = (e) => {
        const file = e.target.files[0];
        if (!file || !currentMember) return;

        setCroppingProfile({ ...currentMember, _own: true });
        setCropFile(file);
    };

    const handleRemoveOwnPhoto = async () => {
        if (!currentMember) return;
        if (!window.confirm("Are you sure you want to delete your photo?")) return;

        setRemoving(true);

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE}/api/me/photo`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (!data.ok) setProfileMsg(data?.error || "Photo remove failed.");
            await loadMyProfile();
        } finally {
            setRemoving(false);
        }
    };

    // Filtering + sorting
    let filtered = profiles[tab].filter((p) =>
        [
            p.primaryid,
            p.lastname,
            p.firstname,
            p.alias,
            p.rootchapter,
            p.domname,
            p.batchname,
            p.yearserved,
        ]
            .join(" ")
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    if (tab === "Chairman") {
        filtered.sort((a, b) => {
            const yA = parseInt((a.yearserved || "0").split("-")[0]);
            const yB = parseInt((b.yearserved || "0").split("-")[0]);
            return sortOrder === "year-asc" ? yA - yB : yB - yA;
        });
    } else {
        filtered.sort((a, b) => {
            const aL = (a.lastname || "").toLowerCase();
            const bL = (b.lastname || "").toLowerCase();
            return sortOrder === "asc"
                ? aL.localeCompare(bL)
                : bL.localeCompare(aL);
        });
    }

    const totalPages = Math.ceil(filtered.length / pageSize);
    const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

    const pageCount = totalPages || 1;
    const goToPage = (nextPage) => {
        const safePage = Math.min(Math.max(nextPage, 1), pageCount);
        setPage(safePage);
    };

    const numberedPages = (() => {
        const compactMobile =
            typeof window !== "undefined" &&
            window.matchMedia("(max-width: 480px)").matches;

        if (compactMobile) {
            const groupSize = 3;
            const groupStart = Math.floor((page - 1) / groupSize) * groupSize + 1;
            const groupEnd = Math.min(groupStart + groupSize - 1, pageCount);
            const groupPages = Array.from(
                { length: groupEnd - groupStart + 1 },
                (_, i) => groupStart + i
            );

            if (groupEnd < pageCount) {
                return groupEnd === pageCount - 1
                    ? [...groupPages, pageCount]
                    : [...groupPages, `ellipsis-${groupEnd}-${pageCount}`, pageCount];
            }

            return groupPages;
        }

        if (pageCount <= 9) {
            return Array.from({ length: pageCount }, (_, i) => i + 1);
        }

        const visible = compactMobile
            ? new Set([1, pageCount, page - 1, page, page + 1])
            : new Set([1, pageCount, page - 2, page - 1, page, page + 1, page + 2]);

        if (compactMobile && page <= 4) {
            [2, 3, 4].forEach((p) => visible.add(p));
        }

        if (compactMobile && page >= pageCount - 3) {
            [pageCount - 3, pageCount - 2, pageCount - 1].forEach((p) => visible.add(p));
        }

        if (!compactMobile && page <= 5) {
            [2, 3, 4, 5, 6, 7].forEach((p) => visible.add(p));
        }

        if (!compactMobile && page >= pageCount - 4) {
            [pageCount - 6, pageCount - 5, pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1].forEach((p) => visible.add(p));
        }

        const sorted = [...visible]
            .filter((p) => p >= 1 && p <= pageCount)
            .sort((a, b) => a - b);

        return sorted.flatMap((p, index) => {
            const prev = sorted[index - 1];
            return prev && p - prev > 1 ? [`ellipsis-${prev}-${p}`, p] : [p];
        });
    })();

    const placeholder = `${PUBLIC}/business/DefProPic.png`;
    const myProfileFields = [
        ["lastName", "Last Name"],
        ["firstName", "First Name"],
        ["alias", "Alias"],
        ["birthSurvive", "Birth/Survive"],
        ["batchName", "Batch Name"],
        ["rootchapter", "Root Chapter"],
        ["gtglt", "GT/GLT"],
        ["mwwmi", "MWW/MI"],
        ["welcome", "Welcome To"],
        ["domName", "Verified By"],
        ["mobile", "Mobile (Optional)"],
        ["email", "Email"],
    ];

    // Logout
    const logout = async () => {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("idname");
        localStorage.removeItem("lastView");
        clearAuthDisplay();
        await fetch(`${API_BASE}/api/logout`, { method: "POST" }).catch(() => {});
        window.location.replace(FRONTEND_URL);
    };

    const exitPreview = () => {
        if (onExitPreview) onExitPreview();
    };

    const memberDisplayName = currentMember
        ? `${currentMember.firstname || ""} ${currentMember.lastname || ""}`.trim()
        : currentUser?.idname || "Member";
    const memberRoleLabel = currentMember?.gender ? `${currentMember.gender} Member` : "Member Account";
    const memberPhoto = currentMember
        ? getPhotoPath(currentMember.gender, currentMember.primaryid) || placeholder
        : placeholder;

    return (
        <section className="soal-profile">
            <AnnouncementPopup />

            {/* Upload overlay */}
            {uploading && (
                <div className="upload-overlay">
                    <div className="upload-box">
                        <div className="upload-title">Uploading Photo…</div>

                        <div className="upload-progress-bar">
                            <div
                                className="upload-progress-fill"
                                style={{ width: `${uploadProgress}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove overlay */}
            {removing && (
                <div className="remove-progress-overlay">
                    <div className="remove-spinner"></div>
                    <div className="remove-text">Removing Photo…</div>
                </div>
            )}

            {/* HEADER */}
            <div className="soal-header mobile-stack">
                <button
                    className={`mobile-side-nav-toggle ${mobileNavOpen ? "open" : ""} ${mobileNavTooltipVisible ? "tooltip-visible" : ""}`}
                    type="button"
                    onClick={handleMobileNavToggle}
                    aria-expanded={mobileNavOpen}
                    aria-controls="user-mobile-side-nav"
                    aria-label={mobileNavOpen ? "Hide navigation" : "Show navigation"}
                    data-tooltip={mobileNavOpen ? "Close Navigation" : "Open Navigation"}
                >
                    Navigation
                </button>

                {mobileNavOpen && (
                    <button
                        className="mobile-side-nav-scrim"
                        type="button"
                        aria-label="Close navigation"
                        onClick={() => setMobileNavOpen(false)}
                    />
                )}

                <aside
                    id="user-mobile-side-nav"
                    className={`mobile-side-nav ${mobileNavOpen ? "open" : ""}`}
                    aria-hidden={!mobileNavOpen}
                >
                    <div className="mobile-side-profile">
                        <img
                            src={memberPhoto}
                            onError={(e) => (e.currentTarget.src = placeholder)}
                            alt=""
                        />
                        <div>
                            <strong>{memberDisplayName}</strong>
                            <span>{memberRoleLabel}</span>
                        </div>
                    </div>

                    <div className="mobile-side-links">
                        {isAdminPreview && (
                            <button className="mobile-side-link preview-link" onClick={exitPreview} type="button">
                                Back to Admin
                            </button>
                        )}
                        <button className={`mobile-side-link ${view === "myprofile" ? "active" : ""}`} onClick={() => handleMobileViewChange("myprofile")} type="button">
                            My Profile
                        </button>
                        <button className={`mobile-side-link ${view === "profile" ? "active" : ""}`} onClick={() => handleMobileViewChange("profile")} type="button">
                            Members
                        </button>
                        <button className={`mobile-side-link ${view === "postfeed" ? "active" : ""}`} onClick={() => handleMobileViewChange("postfeed")} type="button">
                            Post Feed
                        </button>
                        <button className={`mobile-side-link ${view === "calendar" ? "active" : ""}`} onClick={() => handleMobileViewChange("calendar")} type="button">
                            Calendar
                        </button>
                        <button className={`mobile-side-link ${view === "report" ? "active" : ""}`} onClick={() => handleMobileViewChange("report")} type="button">
                            Reports
                        </button>
                        <button className={`mobile-side-link ${view === "voting" ? "active" : ""}`} onClick={() => handleMobileViewChange("voting")} type="button">
                            Voters
                        </button>
                    </div>

                    <button className="mobile-side-logout" onClick={logout} type="button">
                        Logout
                    </button>
                </aside>

                {/* Logout on top */}
                <div className="logout-row">
                    {isAdminPreview && (
                        <button className="header-btn preview-exit-btn" onClick={exitPreview} type="button">
                            Back to Admin
                        </button>
                    )}
                    <button className="header-btn logout-mobile" onClick={logout}>
                        Logout
                    </button>
                </div>

                {/* Navigation buttons */}
                <div className="header-buttons nav-row">
                    <button
                        className={`header-btn ${view === "myprofile" ? "active" : ""}`}
                        onClick={() => handleViewChange("myprofile")}
                    >
                        My Profile
                    </button>
                    <button
                        className={`header-btn ${view === "profile" ? "active" : ""}`}
                        onClick={() => handleViewChange("profile")}
                    >
                        Members
                    </button>
                    <button
                        className={`header-btn ${view === "postfeed" ? "active" : ""}`}
                        onClick={() => handleViewChange("postfeed")}
                    >
                        Post Feed
                    </button>
                    <button
                        className={`header-btn ${view === "report" ? "active" : ""}`}
                        onClick={() => handleViewChange("report")}
                    >
                        Report
                    </button>
                    <button
                        className={`header-btn ${view === "voting" ? "active" : ""}`}
                        onClick={() => handleViewChange("voting")}
                    >
                        Voters
                    </button>
                    <button
                        className={`header-btn ${view === "calendar" ? "active" : ""}`}
                        onClick={() => handleViewChange("calendar")}
                    >
                        Calendar
                    </button>
                </div>

            </div>


            {/* MY PROFILE VIEW */}
            {view === "myprofile" && (
                <div className="my-profile-panel">
                    {!currentMember ? (
                        <div className="profile-message">
                            {profileMsg || "Loading your member profile..."}
                        </div>
                    ) : (
                        <>
                            <div className="my-profile-hero">
                                <div className="my-profile-photo">
                                    <img
                                        src={getPhotoPath(currentMember.gender, currentMember.primaryid) || placeholder}
                                        onError={(e) => (e.currentTarget.src = placeholder)}
                                        onClick={() =>
                                            setEnlargedImage(
                                                getPhotoPath(currentMember.gender, currentMember.primaryid) || placeholder
                                            )
                                        }
                                        alt="My profile"
                                    />
                                </div>

                                <div className="my-profile-summary">
                                    <div className="muted">{currentMember.gender} Member</div>
                                    <h2>
                                        {currentMember.firstname} {currentMember.lastname}
                                    </h2>
                                    <div className="muted">ID: {currentMember.primaryid}</div>
                                    {currentUser?.idname && (
                                        <div className="muted">Username: {currentUser.idname}</div>
                                    )}
                                    {currentUser?.email && (
                                        <div className="muted">Email: {currentUser.email}</div>
                                    )}
                                    {currentUser?.mobile && (
                                        <div className="muted">Mobile: {currentUser.mobile}</div>
                                    )}
                                </div>
                            </div>

                            <div className="photo-action-bar">
                                <label className="photo-btn">
                                    Upload Photo
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleOwnPhotoUpload}
                                        style={{ display: "none" }}
                                    />
                                </label>

                                <button className="photo-btn remove-btn" onClick={handleRemoveOwnPhoto}>
                                    Remove
                                </button>
                            </div>

                            <div className="my-profile-form">
                                {myProfileFields.map(([key, label]) => (
                                    <label key={key}>
                                        <span>{label}</span>
                                        <input
                                            type={key === "email" ? "email" : key === "mobile" ? "tel" : "text"}
                                            inputMode={key === "mobile" ? "numeric" : undefined}
                                            pattern={key === "mobile" ? "[0-9]*" : undefined}
                                            value={myEdit?.[key] || ""}
                                            onChange={(e) => updateMyEdit(key, e.target.value)}
                                            readOnly={!isEditingProfile}
                                        />
                                    </label>
                                ))}
                            </div>

                            {profileMsg && <div className="profile-message">{profileMsg}</div>}

                            <div className="profile-actions">
                                {isEditingProfile ? (
                                    <button
                                        className="header-btn active"
                                        onClick={saveMyProfile}
                                        disabled={savingProfile}
                                    >
                                        {savingProfile ? "Saving..." : "Save Profile"}
                                    </button>
                                ) : (
                                    <button
                                        className="header-btn active"
                                        onClick={() => {
                                            setProfileMsg("");
                                            setIsEditingProfile(true);
                                        }}
                                    >
                                        Edit Profile
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}


            {/* PROFILE VIEW */}
            {view === "profile" && (
                <>
                    <div className="tabs">
                        {["Phi", "Sigma", "Chairman"].map((t) => (
                            <button
                                key={t}
                                className={tab === t ? "active" : ""}
                                onClick={() => {
                                    setTab(t);
                                    localStorage.setItem("lastTab", t);
                                }}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    <div className="controls-bar">
                        {/* LEFT */}
                        <div className="left-controls">
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                <option value={20}>20 | Show entries</option>
                                <option value={50}>50 | Show entries</option>
                                <option value={100}>100 | Show entries</option>
                            </select>

                            <select
                                className="sort-dropdown"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                            >
                                {tab === "Chairman" ? (
                                    <>
                                        <option value="year-desc">Year Served: High → Low</option>
                                        <option value="year-asc">Year Served: Low → High</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="asc">Sort A → Z</option>
                                        <option value="desc">Sort Z → A</option>
                                    </>
                                )}
                            </select>
                        </div>

                        {/* CENTER */}
                        <div className="center-controls">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                            />

                            <div className="counters">
                                <span>Total Phi: {profiles.Phi.length}</span>
                                <span>Total Sigma: {profiles.Sigma.length}</span>
                            </div>
                        </div>

                        {/* RIGHT */}
                        <div className="right-controls">
                            <div className="pagination">
                                <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                                    Prev
                                </button>
                                <span>
                                    Page {page} of {totalPages || 1}
                                </span>
                                <button
                                    disabled={page === totalPages}
                                    onClick={() => setPage(page + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* GRID */}
                    <div className={`card-grid-${tab === "Chairman" ? "4" : "5"} centered`}>
                        {paginated.map((p) => (
                            <div
                                className={`person-card ${tab === "Chairman" ? "chairman-card" : ""
                                    }`}
                                key={p.primaryid}
                                onClick={() => {
                                    setSelectedProfileTab(tab);
                                    setSelectedProfile(p);
                                }}
                            >
                                <div
                                    className={`profile-image ${tab === "Chairman" ? "chairman-photo" : ""
                                        }`}
                                >
                                    <img
                                        src={getPhotoPath(tab, p.primaryid) || placeholder}
                                        onError={(e) => (e.currentTarget.src = placeholder)}
                                        alt="profile"
                                    />
                                </div>

                                <div className="person-info">
                                    <div className="muted">ID: {p.primaryid}</div>
                                    <h4>
                                        {p.lastname}, {p.firstname}
                                    </h4>
                                    <div className="muted">{p.alias ? `"${p.alias}"` : "—"}</div>
                                    <div className="muted">
                                        Root Chapter: {p.rootchapter || "—"}
                                    </div>
                                    {tab === "Chairman" && (
                                        <div className="vip-year">
                                            Year Served: {p.yearserved || "—"}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {pageCount > 1 && (
                        <nav className="bottom-page-nav" aria-label="Member card pages">
                            <button
                                type="button"
                                disabled={page === 1}
                                onClick={() => goToPage(page - 1)}
                            >
                                ← Prev
                            </button>

                            {numberedPages.map((item) =>
                                typeof item === "number" ? (
                                    <button
                                        key={item}
                                        type="button"
                                        className={item === page ? "active" : ""}
                                        aria-current={item === page ? "page" : undefined}
                                        onClick={() => goToPage(item)}
                                    >
                                        {item}
                                    </button>
                                ) : (
                                    <span key={item} className="page-ellipsis">...</span>
                                )
                            )}

                            <button
                                type="button"
                                disabled={page === pageCount}
                                onClick={() => goToPage(page + 1)}
                            >
                                Next →
                            </button>
                        </nav>
                    )}
                </>
            )}

            {view === "postfeed" && <PostFeed onOpenMemberProfile={openPostFeedMemberProfile} />}

            {/* REPORT VIEW */}
            {view === "report" && (
                <div className="report-section">
                    <h3 className="report-title">📂 Uploaded Reports</h3>
                    <ul>
                        {reports.length > 0 ? (
                            reports.map((r) => {
                                const uploadedDate = new Date(r.uploadedat).toLocaleString(
                                    "en-US",
                                    {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    }
                                );

                                return (
                                    <li key={r.filename} className="report-item">
                                        <a
                                            className="report-link"
                                            href={
                                                r.url.startsWith("http")
                                                    ? r.url
                                                    : `${API_BASE}${r.url}`
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {r.title || r.filename}
                                        </a>

                                        <div className="date-tag">📅 {uploadedDate}</div>
                                    </li>
                                );
                            })
                        ) : (
                            <li>No reports available yet.</li>
                        )}
                    </ul>
                </div>
            )}

            {/* VOTERS VIEW */}
            {view === "voting" && <Voters currentMember={currentMember} currentUser={currentUser} />}

            {/* CALENDAR VIEW */}
            {view === "calendar" && <UserCalendar />}

            {/* PROFILE MODAL */}
            {selectedProfile && (
                <div
                    className="modal-overlay"
                    onClick={() => {
                        setSelectedProfile(null);
                        setSelectedProfileTab(null);
                    }}
                >
                    <div className="modal-box profile-modal-card" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="close-button"
                            onClick={() => {
                                setSelectedProfile(null);
                                setSelectedProfileTab(null);
                            }}
                        >
                            ✖
                        </button>

                        <div className="modal-top">
                            <img
                                src={getPhotoPath(selectedProfileTab || tab, selectedProfile.primaryid) || placeholder}
                                alt="profile"
                                onError={(e) => (e.currentTarget.src = placeholder)}
                                style={{ cursor: "zoom-in" }}
                                onClick={() =>
                                    setEnlargedImage(
                                        getPhotoPath(selectedProfileTab || tab, selectedProfile.primaryid) ||
                                        placeholder
                                    )
                                }
                            />

                            <div className="modal-profile-summary">
                                <div className="modal-profile-name">
                                    {selectedProfile.firstname} {selectedProfile.lastname}
                                </div>
                                {selectedProfile.alias && (
                                    <div className="muted">Alias: "{selectedProfile.alias}"</div>
                                )}
                                <div className="muted">
                                    ID: {selectedProfile.primaryid}
                                </div>
                                {selectedProfile.mobile && (
                                    <div className="muted">Mobile: {selectedProfile.mobile}</div>
                                )}
                                {selectedProfile.email && (
                                    <div className="muted">Email: {selectedProfile.email}</div>
                                )}
                            </div>
                        </div>

                        {/* PHOTO ACTION BUTTONS */}
                        {false && (tab === "Phi" || tab === "Sigma") && (
                            <div className="photo-action-bar">
                                <label className="photo-btn">
                                    📤 Upload Photo
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handlePhotoUpload(e, selectedProfile)}
                                        style={{ display: "none" }}
                                    />
                                </label>

                                <button
                                    className="photo-btn remove-btn"
                                    onClick={() => handleRemovePhoto(selectedProfile)}
                                >
                                    🗑 Remove
                                </button>
                            </div>
                        )}

                        <div className="modal-grid profile-detail-grid">
                            {Object.entries(selectedProfile)
                                .filter(([k]) =>
                                    ![
                                        "id",
                                        "primaryid",
                                        "lastname",
                                        "firstname",
                                        "alias",
                                        "email",
                                        "mobile",
                                    ].includes(k.toLowerCase())
                                )
                                .map(([k, v]) => (
                                    <div key={k}>
                                        <strong>{labelMap[k.toLowerCase()] || k}:</strong>{" "}
                                        {v || "—"}
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}

            {/* CROP MODAL */}
            {cropFile && (
                <CropModal
                    file={cropFile}
                    uploading={uploading} // loader inside modal
                    onCancel={() => {
                        setCroppingProfile(null);
                        setCropFile(null);
                    }}
                    onCropComplete={finishCropAndUpload}
                />
            )}

            {/* ENLARGED IMAGE */}
            {enlargedImage && (
                <div className="image-modal" onClick={() => setEnlargedImage(null)}>
                    <span className="close-btn">✖</span>
                    <div className="modal-circle" onClick={(e) => e.stopPropagation()}>
                        <img src={enlargedImage} alt="Enlarged Profile" />
                    </div>
                </div>
            )}
        </section>
    );
}
